import { type NavLink, type NavLinkData } from '@/features/cms/components/CMS/types'
import { css } from '@/features/shared/styles'
import styled from 'styled-components'
import { DISPLAY_FORMAT } from '@/features/shared/constants/analytics'
import { LazyLoadObserve, ScrollIntoView } from '@/features/shared/components/ScrollIntoView'
import { Body } from '@shipt/design-system-typography'
import {
  parseCategoryId,
  parseCategoryIds,
  parseCategoryName,
} from '@/features/cms/components/CMS/utils/parse'
import { useCMSTrackingEvents } from '@/features/cms/components/CMS/CMSContext'
import { BREAKPOINTS, screenSizes } from '@shipt/design-system-themes'
import { Image } from '@/features/shared/elements/Image/NextImage'
import { getCMSWrapperProps } from '@/features/cms/components/CMS/utils/getCMSWrapperProps'
import { Carousel } from '@shipt/design-system-carousel'
import { GridLayoutContainer } from '@/features/shared/styles/layout'
import { TransparentButton } from '@/features/shared/elements/Buttons'
import { useIsPage } from '@/features/shared/hooks/useIsPage'
import { NextLink } from '@/features/shared/elements/Link'
import { useHandleDialog } from '@/features/shared/state/Dialog/useHandleDialog'
import { DynamicGlobalStoreSelectionDrawer } from '@/features/shop/components/Drawer/GlobalStoreSelectionDrawer/DynamicGlobalStoreSelectionDrawer'
import { EAGER_LOAD_SHELF_INDEX } from '@/features/shared/constants/global'
import { theme } from '@/features/shared/constants/shiptTheme'

const CategoryImageAndName = ({
  backgroundColor,
  image,
  name,
}: {
  name: string
  backgroundColor?: string
  image: string
}) => {
  return (
    <>
      <CategoryBrowseTile backgroundColor={backgroundColor}>
        <CategoryImage
          alt={`${name} category image`}
          src={image}
          fill
          sizes={`${screenSizes.tablet} 150px, 250px`}
          fallbackType="category"
        />
      </CategoryBrowseTile>
      <CategoryNameWrapper>
        <CategoryName size="md">{name}</CategoryName>
      </CategoryNameWrapper>
    </>
  )
}

const CategoryTile = ({
  backgroundColor,
  image,
  name,
  onClick,
  navItem,
  displaySublocation,
}: {
  backgroundColor?: string
  image: string
  name: string
  onClick: () => void
  navItem: NavLinkData
  displaySublocation: string
}) => {
  const { openDialog } = useHandleDialog()
  const { GLOBAL_HOMEPAGE } = useIsPage()

  const renderCategoryCTA = () => {
    const imageAndName = (
      <CategoryImageAndName backgroundColor={backgroundColor} image={image} name={name} />
    )
    return (
      <CategoryLink
        href={navItem.link.url}
        onClick={onClick}
        aria-label={`click to select ${name}`}
      >
        {imageAndName}
      </CategoryLink>
    )
  }

  return navItem.available_stores?.length && GLOBAL_HOMEPAGE ? (
    <CategoryButton
      onClick={() => {
        onClick()
        openDialog(DynamicGlobalStoreSelectionDrawer, {
          displaySublocation,
          stores: navItem.available_stores,
          link: navItem.link,
          heading: navItem.heading,
          subheading: navItem.link.text,
          trackingContent: 'category_retailers_panel',
        })
      }}
      aria-label={`click to select ${name}`}
    >
      <CategoryImageAndName backgroundColor={backgroundColor} image={image} name={name} />
    </CategoryButton>
  ) : (
    renderCategoryCTA()
  )
}

const layoutConfigMapping = {
  small: {
    '0': {
      slidesPerGroup: 3.5,
      slidesPerView: 3.5,
    },
    [BREAKPOINTS.SMALL]: {
      slidesPerGroup: 5.5,
      slidesPerView: 5.5,
    },
    [BREAKPOINTS.MEDIUM]: {
      slidesPerGroup: 6,
      slidesPerView: 6,
    },
    [BREAKPOINTS.LARGE]: {
      slidesPerGroup: 7,
      slidesPerView: 7,
    },
    [BREAKPOINTS.XLARGE]: {
      slidesPerGroup: 7,
      slidesPerView: 7,
    },
  },
  medium: {
    '0': {
      slidesPerGroup: 2.5,
      slidesPerView: 2.5,
    },
    [BREAKPOINTS.SMALL]: {
      slidesPerGroup: 4.5,
      slidesPerView: 4.5,
    },
    [BREAKPOINTS.MEDIUM]: {
      slidesPerGroup: 5,
      slidesPerView: 5,
    },
    [BREAKPOINTS.LARGE]: {
      slidesPerGroup: 6,
      slidesPerView: 6,
    },
    [BREAKPOINTS.XLARGE]: {
      slidesPerGroup: 6,
      slidesPerView: 6,
    },
  },
}

export const StandardNavRedesign = ({
  content_type_id,
  id,
  index: shelfIndex,
  heading,
  nav_links,
  displaySublocation,
  size = 'small',
}: {
  content_type_id: string
  id: string
  index: number
  heading?: string
  nav_links: NavLink[]
  displaySublocation: string
  size?: 'small' | 'medium'
}) => {
  const { trackCMSElementViewed, trackCMSElementClicked } = useCMSTrackingEvents()

  const renderCategoryTiles = () =>
    nav_links.map(({ data: navItem }, index) => {
      return (
        <CategoryTile
          key={navItem.heading}
          onClick={() => {
            const eventProperties = navItem.available_stores
              ? {
                  type: 'button',
                }
              : {
                  shelf_index: shelfIndex,
                  next_location: navItem.link.url,
                  category_id:
                    parseCategoryId(navItem.link.url) ?? parseCategoryIds(navItem.link.url),
                  category_name: parseCategoryName(navItem.link.url),
                }
            trackCMSElementClicked({
              display_format: DISPLAY_FORMAT.HORIZONTAL,
              shelf_name: heading,
              display_sublocation: displaySublocation,
              list_index: index,
              content: navItem.heading,
              ...eventProperties,
            })
          }}
          backgroundColor={navItem.background_color}
          image={navItem.image.src}
          name={navItem.heading}
          navItem={navItem}
          displaySublocation={displaySublocation}
        />
      )
    })

  return (
    <CarouselWrapper {...getCMSWrapperProps({ content_type_id, id })}>
      <LazyLoadObserve
        minHeight={200}
        eagerLoad={shelfIndex <= EAGER_LOAD_SHELF_INDEX}
        className="fullWidth contentAtTablet"
      >
        <ScrollIntoView
          onInView={() => {
            trackCMSElementViewed({
              shelf_index: shelfIndex,
              shelf_name: heading,
              display_sublocation: displaySublocation,
            })
          }}
        >
          <Carousel
            title={{
              content: heading ?? 'Shop by category',
              isHidden: !heading,
            }}
            layoutConfig={layoutConfigMapping[size]}
            className="categories-carousel"
            hasInlineControls
          >
            {renderCategoryTiles()}
          </Carousel>
        </ScrollIntoView>
      </LazyLoadObserve>
    </CarouselWrapper>
  )
}

const CarouselWrapper = styled(GridLayoutContainer)`
  margin: 0 0 1.5rem;

  @media ${screenSizes.tablet} {
    margin-top: 0.5rem;
  }

  & .categories-carousel {
    grid-column: full-width;

    @media ${screenSizes.tablet} {
      grid-column: content;
    }
  }
`

const CategoryStyles = css`
  flex-grow: 1;
  align-self: start;
  text-align: left;
  width: 100%;

  &:focus-visible {
    outline: solid 2px ${theme.color.focus.default};
    border-radius: 12px;
  }
`

const CategoryName = styled(Body)`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`

const CategoryNameWrapper = styled.div`
  display: inline-grid;
`

const CategoryButton = styled(TransparentButton)`
  ${CategoryStyles}
`

const CategoryLink = styled(NextLink)`
  ${CategoryStyles}
`

const CategoryBrowseTile = styled.div<{
  backgroundColor?: string
}>`
  overflow: hidden;
  position: relative;
  background-color: ${({ backgroundColor }) => `${backgroundColor}`};
  display: flex;
  flex-direction: column;
  border: 0.5px solid #0000001a;
  border-radius: 8px;
  aspect-ratio: 4 / 3;
  width: 100%;

  @supports not (aspect-ratio: 4 / 3) {
    &::before {
      float: left;
      padding-top: 56.25%;
      content: '';
    }

    &::after {
      display: block;
      content: '';
      clear: both;
    }
  }

  &::after {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    background-color: rgb(0 0 0 / 100%);
    border-radius: 7px;
    opacity: 0;
  }

  ${CategoryButton}:hover &::after {
    opacity: 0.2;
  }

  ${CategoryLink}:hover &::after {
    opacity: 0.2;
  }
`

const CategoryImage = styled(Image)`
  object-fit: contain;
`
